import React, { useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import { playSoundEffect } from '../hooks/useSoundEffects';
import './LethalDeathParticleDissipation.css';

export const LETHAL_DEATH_WHITE_WASH_RATIO = 0.32;
export const LETHAL_DEATH_PARTICLE_BASE_MS = 720;

export const LETHAL_DEATH_DISSIPATE_SOUND = '/sounds/ascend1.mp3';
export const LETHAL_DEATH_DISSIPATE_SOUND_GAIN = 0.55;

export function playLethalDeathDissipateSound() {
  playSoundEffect(LETHAL_DEATH_DISSIPATE_SOUND, LETHAL_DEATH_DISSIPATE_SOUND_GAIN);
}

const PARTICLE_COUNT = 110;
const RISE_DISTANCE = 230;

function easeOutCubic(t) {
  return 1 - Math.pow(1 - t, 3);
}

function clamp01(value) {
  return value < 0 ? 0 : value > 1 ? 1 : value;
}

/** Pre-render a soft radial glow sprite once so per-frame draws stay cheap. */
function buildGlowSprite(rgb) {
  const size = 64;
  const sprite = document.createElement('canvas');
  sprite.width = size;
  sprite.height = size;
  const ctx = sprite.getContext('2d');
  if (!ctx) {
    throw new Error('LethalDeathParticleDissipation: unable to build glow sprite');
  }
  const gradient = ctx.createRadialGradient(size / 2, size / 2, 0, size / 2, size / 2, size / 2);
  gradient.addColorStop(0, `rgba(${rgb}, 1)`);
  gradient.addColorStop(0.35, `rgba(${rgb}, 0.85)`);
  gradient.addColorStop(0.7, `rgba(${rgb}, 0.25)`);
  gradient.addColorStop(1, `rgba(${rgb}, 0)`);
  ctx.fillStyle = gradient;
  ctx.fillRect(0, 0, size, size);
  return sprite;
}

function createParticles(anchorRect) {
  const particles = [];
  const cx = anchorRect.left + anchorRect.width / 2;
  for (let i = 0; i < PARTICLE_COUNT; i += 1) {
    // Bias spawn points toward the card silhouette, denser near the vertical core.
    const edgeRoll = Math.random();
    let originX;
    let originY;
    if (edgeRoll < 0.45) {
      originX = anchorRect.left + Math.random() * anchorRect.width;
      originY = anchorRect.top + Math.random() * anchorRect.height;
    } else if (edgeRoll < 0.75) {
      originX = anchorRect.left + (Math.random() < 0.5 ? 0 : anchorRect.width);
      originY = anchorRect.top + Math.random() * anchorRect.height;
    } else {
      originX = anchorRect.left + Math.random() * anchorRect.width;
      originY = anchorRect.top + (Math.random() < 0.5 ? 0 : anchorRect.height);
    }

    const isSpark = Math.random() < 0.38;
    const horizontalBias = (originX - cx) / (anchorRect.width / 2 || 1);

    particles.push({
      x: originX,
      y: originY,
      spawnDelay: Math.random() * 0.28,
      rise: (0.7 + Math.random() * 0.6) * RISE_DISTANCE,
      drift: horizontalBias * 18 + (Math.random() - 0.5) * 26,
      swayAmp: 6 + Math.random() * 16,
      swayFreq: 1.5 + Math.random() * 2.5,
      swayPhase: Math.random() * Math.PI * 2,
      size: isSpark ? 2 + Math.random() * 4 : 7 + Math.random() * 14,
      twinkleFreq: 4 + Math.random() * 6,
      brightness: 0.55 + Math.random() * 0.45,
      cool: Math.random() < 0.4,
    });
  }
  return particles;
}

/**
 * Protoss-style lethal death dissipation: glowing energy motes rise, sway, twinkle, and fade upward.
 */
export function LethalDeathParticleDissipation({ anchorRect, durationMs, onComplete }) {
  const canvasRef = useRef(null);
  const completedRef = useRef(false);
  const onCompleteRef = useRef(onComplete);
  onCompleteRef.current = onComplete;

  useEffect(() => {
    if (!anchorRect || !durationMs) {
      throw new Error('LethalDeathParticleDissipation requires anchorRect and durationMs');
    }

    const canvas = canvasRef.current;
    if (!canvas) {
      throw new Error('LethalDeathParticleDissipation: canvas ref missing');
    }

    completedRef.current = false;
    const ctx = canvas.getContext('2d');
    if (!ctx) {
      throw new Error('LethalDeathParticleDissipation: 2d context unavailable');
    }

    const dpr = window.devicePixelRatio || 1;
    canvas.width = Math.floor(window.innerWidth * dpr);
    canvas.height = Math.floor(window.innerHeight * dpr);
    canvas.style.width = `${window.innerWidth}px`;
    canvas.style.height = `${window.innerHeight}px`;
    ctx.setTransform(dpr, 0, 0, dpr, 0, 0);

    const warmSprite = buildGlowSprite('255, 252, 240');
    const coolSprite = buildGlowSprite('200, 232, 255');
    const particles = createParticles(anchorRect);
    const cx = anchorRect.left + anchorRect.width / 2;
    const cy = anchorRect.top + anchorRect.height / 2;
    const bloomRadius = Math.max(anchorRect.width, anchorRect.height) * 0.9;

    const startTime = performance.now();
    let frameId = null;

    const finish = () => {
      if (completedRef.current) return;
      completedRef.current = true;
      onCompleteRef.current?.();
    };

    const draw = (now) => {
      const elapsed = now - startTime;
      const progress = clamp01(elapsed / durationMs);

      ctx.clearRect(0, 0, window.innerWidth, window.innerHeight);
      ctx.globalCompositeOperation = 'lighter';

      // Initial bloom flash that fades within the first quarter of the effect.
      const bloomProgress = clamp01(progress / 0.28);
      if (bloomProgress < 1) {
        const bloomAlpha = (1 - bloomProgress) * 0.55;
        const bloom = ctx.createRadialGradient(cx, cy, 0, cx, cy, bloomRadius);
        bloom.addColorStop(0, `rgba(255, 255, 255, ${bloomAlpha})`);
        bloom.addColorStop(0.5, `rgba(210, 235, 255, ${bloomAlpha * 0.5})`);
        bloom.addColorStop(1, 'rgba(210, 235, 255, 0)');
        ctx.fillStyle = bloom;
        ctx.beginPath();
        ctx.arc(cx, cy, bloomRadius, 0, Math.PI * 2);
        ctx.fill();
      }

      for (let i = 0; i < particles.length; i += 1) {
        const p = particles[i];
        const local = clamp01((progress - p.spawnDelay) / (1 - p.spawnDelay));
        if (local <= 0) continue;

        const climb = easeOutCubic(local);
        const sway = Math.sin(local * p.swayFreq * Math.PI * 2 + p.swayPhase) * p.swayAmp;
        const x = p.x + p.drift * climb + sway * (0.3 + climb);
        const y = p.y - p.rise * climb;

        // Fade in fast, then taper out toward the top.
        const fadeIn = clamp01(local / 0.18);
        const fadeOut = 1 - clamp01((local - 0.45) / 0.55);
        const twinkle = 0.75 + 0.25 * Math.sin(local * p.twinkleFreq * Math.PI * 2 + p.swayPhase);
        const alpha = fadeIn * fadeOut * twinkle * p.brightness;
        if (alpha <= 0.01) continue;

        const scale = p.size * (1.1 - 0.55 * climb);
        const sprite = p.cool ? coolSprite : warmSprite;

        ctx.globalAlpha = alpha;
        ctx.drawImage(sprite, x - scale, y - scale, scale * 2, scale * 2);
      }

      ctx.globalAlpha = 1;
      ctx.globalCompositeOperation = 'source-over';

      if (progress >= 1) {
        finish();
        return;
      }
      frameId = requestAnimationFrame(draw);
    };

    frameId = requestAnimationFrame(draw);

    return () => {
      if (frameId != null) {
        cancelAnimationFrame(frameId);
      }
    };
  }, [anchorRect, durationMs]);

  return createPortal(
    <canvas
      ref={canvasRef}
      className="lethal-death-particle-canvas"
      aria-hidden="true"
    />,
    document.body
  );
}

export default LethalDeathParticleDissipation;
